import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiPlay, FiPause, FiRotateCcw, FiVolume2, FiVolumeX, FiSettings, FiMaximize, FiCheckCircle } = FiIcons;

export default function VideoControls({ 
  playing, onTogglePlay, progress, duration, 
  onSeek, volume, onVolumeChange, muted, onToggleMute,
  playbackRate, onRateChange, isCompleted, showControls 
}) {
  const formatTime = (seconds) => {
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = String(date.getUTCSeconds()).padStart(2, '0');
    if (hh) return `${hh}:${String(mm).padStart(2, '0')}:${ss}`;
    return `${mm}:${ss}`;
  };

  return (
    <motion.div 
      initial={false}
      animate={{ opacity: showControls ? 1 : 0, y: showControls ? 0 : 10 }}
      className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-6 pt-16 z-10"
    >
      {/* Progress Bar */}
      <div className="relative group/progress h-1.5 bg-white/20 rounded-full mb-6 cursor-pointer" onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        onSeek((e.clientX - rect.left) / rect.width);
      }}>
        <div className="absolute h-full bg-indigo-500 rounded-full" style={{ width: `${progress * 100}%` }} />
        <div className="absolute h-4 w-4 bg-white rounded-full -top-1.5 shadow-xl opacity-0 group-hover/progress:opacity-100 transition-opacity" style={{ left: `calc(${progress * 100}% - 8px)` }} />
      </div>

      <div className="flex items-center justify-between text-white">
        <div className="flex items-center gap-6">
          <button onClick={onTogglePlay} className="hover:text-indigo-400 transition-colors">
            <SafeIcon icon={playing ? FiPause : FiPlay} className="text-2xl" />
          </button>
          
          <div className="flex items-center gap-3 group/volume relative">
            <button onClick={onToggleMute} className="hover:text-indigo-400">
              <SafeIcon icon={muted || volume === 0 ? FiVolumeX : FiVolume2} className="text-xl" />
            </button>
            <input 
              type="range" min="0" max="1" step="0.1" value={volume} 
              onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
              className="w-0 group-hover/volume:w-20 transition-all accent-indigo-500 h-1"
            />
          </div>

          <span className="text-xs font-mono text-slate-300">
            {formatTime(progress * duration)} <span className="text-slate-600">/</span> {formatTime(duration)}
          </span>
        </div>

        <div className="flex items-center gap-6">
          {isCompleted && (
            <div className="flex items-center gap-2 text-emerald-400 text-[10px] font-bold bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 uppercase tracking-widest">
              <SafeIcon icon={FiCheckCircle} /> Finished
            </div>
          )}

          <div className="relative group/speed">
            <button className="text-xs font-bold hover:text-indigo-400 px-2 py-1 bg-white/5 rounded">
              {playbackRate}x
            </button>
            <div className="absolute bottom-full right-0 mb-2 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hidden group-hover/speed:block min-w-[80px]">
              {[0.5, 1, 1.5, 2].map(rate => (
                <button 
                  key={rate} onClick={() => onRateChange(rate)}
                  className={`w-full px-4 py-2 text-xs text-left hover:bg-indigo-600 ${playbackRate === rate ? 'text-indigo-400 font-bold' : ''}`}
                >
                  {rate}x
                </button>
              ))}
            </div>
          </div>
          
          <button className="hover:text-indigo-400"><SafeIcon icon={FiMaximize} className="text-xl" /></button>
        </div>
      </div>
    </motion.div>
  );
}